/**
 * API Client Core — base fetch, error class, session listeners, and shared types
 */

import type {
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/browser';

// Types for API responses
export interface Manager {
  id: string;
  email: string;
  displayName?: string;
  createdAt: string;
}

export interface Portfolio {
  id: string;
  name: string;
  managerId: string;
  createdAt: string;
}

export interface ManagerSession {
  identity: {
    id: string;
    email: string;
    displayName?: string;
  };
  portfolios: Portfolio[];
}

export interface RegistrationOptions {
  options: PublicKeyCredentialCreationOptionsJSON;
  managerId: string;
}

export interface LoginOptions {
  options: PublicKeyCredentialRequestOptionsJSON;
  sessionId: string;
}

export interface ApiError {
  code: string;
  message: string;
  details?: unknown;
}

// API base URL - empty string since routes now include full path
const API_BASE = '';

/**
 * Session expiry event listeners
 */
type SessionExpiredListener = () => void;
const sessionExpiredListeners: SessionExpiredListener[] = [];

/**
 * Register a listener for session expiry events
 */
export function onSessionExpired(listener: SessionExpiredListener): () => void {
  sessionExpiredListeners.push(listener);

  // Return unsubscribe function
  return () => {
    const index = sessionExpiredListeners.indexOf(listener);
    if (index > -1) {
      sessionExpiredListeners.splice(index, 1);
    }
  };
}

/**
 * Reauthentication required event listeners (session too old for sensitive operations)
 */
type ReauthRequiredListener = () => void;
const reauthRequiredListeners: ReauthRequiredListener[] = [];
let reauthReturnTo = '';

/**
 * Register a listener for reauthentication required events
 */
export function onReauthRequired(listener: ReauthRequiredListener): () => void {
  reauthRequiredListeners.push(listener);

  return () => {
    const index = reauthRequiredListeners.indexOf(listener);
    if (index > -1) {
      reauthRequiredListeners.splice(index, 1);
    }
  };
}

/**
 * Get the returnTo URL stored when reauthentication was required
 */
export function getReauthReturnTo(): string {
  return reauthReturnTo;
}

/**
 * Notify all listeners that reauthentication is required
 */
function notifyReauthRequired(): void {
  reauthRequiredListeners.forEach((listener) => listener());
}

/**
 * Trigger reauthentication required from outside the shared apiFetch
 * (e.g. from the local apiFetch in CredentialsPage).
 * Computes the app-relative path and fires the reauth event.
 */
export function triggerReauthRequired(): void {
  const basePath = '/landlord';
  const fullPath = window.location.pathname;
  const appPath = fullPath.startsWith(basePath) ? (fullPath.slice(basePath.length) || '/') : fullPath;
  reauthReturnTo = appPath + window.location.search;
  notifyReauthRequired();
}

/**
 * Account deactivation event listeners
 */
type AccountDeactivatedListener = () => void;
const accountDeactivatedListeners: AccountDeactivatedListener[] = [];

/**
 * Register a listener for account deactivation events.
 * Fired when any API call returns 403 ACCOUNT_DEACTIVATED.
 */
export function onAccountDeactivated(listener: AccountDeactivatedListener): () => void {
  accountDeactivatedListeners.push(listener);

  return () => {
    const index = accountDeactivatedListeners.indexOf(listener);
    if (index > -1) {
      accountDeactivatedListeners.splice(index, 1);
    }
  };
}

function notifyAccountDeactivated(): void {
  accountDeactivatedListeners.forEach((listener) => listener());
}

/**
 * Notify all listeners that session has expired
 */
function notifySessionExpired(): void {
  sessionExpiredListeners.forEach(listener => listener());
}

/**
 * Post-mutation event listeners — fired after any successful POST/PUT/PATCH/DELETE.
 * Register here to react to data changes without per-call invalidation boilerplate.
 */
type MutationListener = () => void;
const mutationListeners: MutationListener[] = [];

export function onMutation(listener: MutationListener): () => void {
  mutationListeners.push(listener);
  return () => {
    const index = mutationListeners.indexOf(listener);
    if (index > -1) {
      mutationListeners.splice(index, 1);
    }
  };
}

function notifyMutation(): void {
  mutationListeners.forEach(listener => listener());
}

/**
 * Shared in-flight refresh promise. Every concurrent 401 awaits this same
 * promise instead of independently calling the refresh endpoint, so that at
 * most one POST /landlord/api/auth/refresh is ever issued per expiry window.
 * Without this, N parallel requests hitting an expired access token would
 * each send the same (not-yet-rotated) refresh cookie; the server treats
 * every request after the first winner as REUSE_DETECTED and force-logs the
 * user out. See docs/domains/authentication/web-ui-review.session-refresh-token-strategy.md.
 */
let refreshInFlight: Promise<boolean> | null = null;

/**
 * Attempt a silent refresh of the access token using the manager_refresh cookie.
 * Returns true only on a 200 response (a fresh access-token cookie was set).
 */
async function attemptSilentRefresh(): Promise<boolean> {
  if (refreshInFlight) {
    return refreshInFlight;
  }

  refreshInFlight = (async () => {
    try {
      const refreshResponse = await fetch(`${API_BASE}/landlord/api/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      });
      return refreshResponse.status === 200;
    } catch {
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

/**
 * Maps a non-2xx JSON error body to an ApiClientError. The server's machine-readable `code` is kept
 * on `serverCode` (a 503 keeps the generic `SERVICE_UNAVAILABLE` `code`, which callers key off).
 */
function buildApiError(status: number, data: Record<string, unknown>): ApiClientError {
  const serverCode = typeof data.code === 'string' ? data.code : undefined;

  // 503 — authorization service (SpiceDB) waking from sleep or temporarily unreachable
  if (status === 503) {
    return new ApiClientError(
      'The service is temporarily unavailable. Please try again in a moment.',
      'SERVICE_UNAVAILABLE',
      status,
      data.details,
      serverCode
    );
  }

  // Backend returns { error: "ERROR_CODE", message: "...", statusCode: ..., timestamp: "..." }
  // error is the code, message is the human-readable message
  return new ApiClientError(
    (typeof data.message === 'string' && data.message) || 'An unexpected error occurred',
    (typeof data.error === 'string' && data.error) || 'UNKNOWN_ERROR',
    status,
    data.details,
    serverCode
  );
}

/**
 * Base fetch wrapper with error handling and credentials
 */
export async function apiFetch<T>(
  endpoint: string,
  options: RequestInit = {},
  isRetry = false
): Promise<T> {
  const url = `${API_BASE}${endpoint}`;

  // Only set Content-Type if there's a body (but not for FormData - browser sets multipart boundary)
  const headers: Record<string, string> = {
    ...options.headers as Record<string, string>,
  };

  if (options.body && !(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }

  const isMutation = ['POST', 'PUT', 'PATCH', 'DELETE'].includes((options.method ?? 'GET').toUpperCase());

  const response = await fetch(url, {
    ...options,
    credentials: 'include', // Include cookies for session management
    headers,
  });

  // Handle non-JSON responses
  const contentType = response.headers.get('content-type');
  if (!contentType?.includes('application/json')) {
    if (!response.ok) {
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }
    if (isMutation) notifyMutation();
    return {} as T;
  }

  const data = await response.json();

  if (!response.ok) {
    // Handle 422 validation errors (warnings that can be overridden)
    if (response.status === 422 && data.validationResult) {
      throw new ApiClientError(
        'Validation warnings',
        'VALIDATION_WARNINGS',
        response.status,
        data
      );
    }

    // Handle 401 Unauthorized - session expired or invalid
    if (response.status === 401) {
      // Attempt one silent refresh + retry of the original request before giving up.
      if (!isRetry && (await attemptSilentRefresh())) {
        return apiFetch<T>(endpoint, options, true);
      }
      // Notify session expiry listeners (will trigger redirect to login)
      notifySessionExpired();
    }

    // Handle 403 ACCOUNT_DEACTIVATED - redirect to billing page
    if (response.status === 403 && data.code === 'ACCOUNT_DEACTIVATED') {
      notifyAccountDeactivated();
      // Return a never-resolving promise so no code runs after the redirect
      return new Promise<never>(() => {});
    }

    // Handle 403 REAUTHENTICATION_REQUIRED - session too old for sensitive operation
    // Check data.code (the specific discriminator), NOT data.error (which is the generic 'Forbidden' label)
    if (response.status === 403 && data.code === 'REAUTHENTICATION_REQUIRED') {
      // Strip BrowserRouter basename so navigate() doesn't double-prefix the path
      const basePath = '/landlord';
      const fullPath = window.location.pathname;
      const appPath = fullPath.startsWith(basePath) ? (fullPath.slice(basePath.length) || '/') : fullPath;
      reauthReturnTo = appPath + window.location.search;
      notifyReauthRequired();
      // Return a never-resolving promise so no code runs after the redirect
      return new Promise<never>(() => {});
    }

    throw buildApiError(response.status, data);
  }

  if (isMutation) notifyMutation();
  return data as T;
}

/**
 * GET a binary file (a zip, a PDF) and return the successful Response for the caller to read. Like
 * apiFetch, a 401 gets one silent refresh + retry before session expiry is announced; any other
 * failure rejects with an ApiClientError built from the JSON error body.
 */
export async function apiFetchFile(endpoint: string, isRetry = false): Promise<Response> {
  const response = await fetch(`${API_BASE}${endpoint}`, { credentials: 'include' });
  if (response.ok) return response;

  if (response.status === 401) {
    if (!isRetry && (await attemptSilentRefresh())) {
      return apiFetchFile(endpoint, true);
    }
    notifySessionExpired();
  }
  const data = (await response.json().catch(() => ({}))) as Record<string, unknown>;
  throw buildApiError(response.status, data);
}

/** One parsed server-sent event: its `event:` name and its `data:` payload (multi-line data joined by \n). */
export interface ApiStreamEvent {
  event: string;
  data: string;
}

export interface ApiStreamOptions {
  signal?: AbortSignal;
  onEvent: (event: ApiStreamEvent) => void;
}

/** Parses one SSE frame (the text between blank lines); returns null for comment-only frames. */
function parseSseFrame(frame: string): ApiStreamEvent | null {
  let event = 'message';
  const data: string[] = [];
  for (const line of frame.split('\n')) {
    if (line === '' || line.startsWith(':')) continue;
    const separator = line.indexOf(':');
    const field = separator === -1 ? line : line.slice(0, separator);
    const value = separator === -1 ? '' : line.slice(separator + 1).replace(/^ /, '');
    if (field === 'event') event = value;
    else if (field === 'data') data.push(value);
  }
  return data.length === 0 ? null : { event, data: data.join('\n') };
}

/**
 * POST a JSON body and consume the `text/event-stream` response, calling `onEvent` for each event as
 * it arrives. Failures before the first byte are JSON errors and reject with an ApiClientError (a 401
 * gets the same one silent refresh + retry as apiFetch). Resolves when the stream ends; aborting the
 * `signal` rejects with the browser's AbortError.
 */
export async function apiStream(
  endpoint: string,
  body: unknown,
  options: ApiStreamOptions,
  isRetry = false
): Promise<void> {
  const { signal, onEvent } = options;
  const response = await fetch(`${API_BASE}${endpoint}`, {
    method: 'POST',
    credentials: 'include',
    signal,
    headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    if (response.status === 401) {
      if (!isRetry && (await attemptSilentRefresh())) {
        return apiStream(endpoint, body, options, true);
      }
      notifySessionExpired();
    }
    const data = (await response.json().catch(() => ({}))) as Record<string, unknown>;
    throw buildApiError(response.status, data);
  }

  if (!response.body) {
    throw new ApiClientError('Streaming is not supported by this browser', 'STREAM_UNSUPPORTED', response.status);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  const flush = (final: boolean) => {
    const normalized = buffer.replace(/\r\n/g, '\n');
    const frames = normalized.split('\n\n');
    buffer = final ? '' : (frames.pop() ?? '');
    for (const frame of frames) {
      const parsed = parseSseFrame(frame);
      if (parsed) onEvent(parsed);
    }
  };

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      flush(false);
    }
    buffer += decoder.decode();
    flush(true);
  } finally {
    reader.releaseLock();
  }
}

/**
 * Custom error class for API errors
 */
export class ApiClientError extends Error {
  constructor(
    message: string,
    public code: string,
    public status: number,
    public details?: unknown,
    /** The response body's own `code` (e.g. HELP_CHAT_DISABLED), when it carried one. */
    public serverCode?: string
  ) {
    super(message);
    this.name = 'ApiClientError';
  }
}
